// @flow

import * as React from 'react';
import { connect } from 'react-redux';
import { compose, withStateHandlers } from 'recompose';
import numeral from 'numeral';
import styled from '@emotion/styled';
import { get, sumBy } from 'lodash/fp';
import t from 'app/utils/translate';
import { RUNNING } from 'app/constants/lineupStatus';
import * as myTeamsActions from 'app/actions/myTeamsActions';
import Responsive from 'app/components/responsive/Responsive';
import Icon from 'app/components/commons/Icon';
import LineUpHeader from 'app/components/challenge/LineUpHeader';
import Modal from 'app/components/commons/Modal';
import Loader from 'app/components/commons/Loader';
import Button from 'app/components/commons/Button';
import CurrencyIcon from 'app/components/commons/CurrencyIcon';
import ClosedEntityRow from '../../components/challenge/ClosedEntityRow';
import myTeamsSelector from '../../selectors/myTeamsSelector';
import MyTeamsContent from './MyTeamsContent';

type Props = {
  activeSport?: string,
  handleWidgetClick: (id: ?string) => void,
  lineups?: Array<Object>,
  loadLineupChallenges: (lineupId: number, query: Object) => void,
  isLoading: boolean,
  onViewContest: (id: string) => void,
  pollMyLiveLineUps: () => void,
  selectedLineUp?: Object,
  setSelectionHash: (hash: string) => void,
  selectedSelectionHash: ?string,
  stopPollingMyLiveLineUps: () => void,
  widgetPlayerId: ?string
};

const ContestButtonContainer = styled.div(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  backgroundColor: theme.color.basis3
}));

const ContestButton = styled('button')(({ theme }) => ({
  backgroundColor: theme.color.basis3,
  color: theme.color.white,
  borderRadius: 4,
  textTransform: 'uppercase',
  fontSize: theme.fontSize.small,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0.5em 0.5em',
  minWidth: 90
}));

const ChallengeTable = styled.div({
  display: 'flex',
  flexWrap: 'wrap',
  padding: 0,
  width: '100%'
});

const Cell = styled.div(({ theme, order = 1, align, wide }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: align || 'center',
  boxSizing: 'border-box',
  flexGrow: 1,
  padding: theme.spacing.small,
  overflow: 'hidden', // Or flex might break
  listStyle: 'none',
  verticalAlign: 'middle',
  fontSize: theme.fontSize.default,
  borderBottom: `1px solid ${theme.color.lightBackground || theme.color.basis3}`,
  order,
  width: wide ? '40%' : '20%',
  [theme.mediaQuery.mobileOnly]: {
    width: wide ? '100%' : '33.33%'
  }
}));

const Header = styled(Cell)(({ theme }) => ({
  backgroundColor: theme.color.basis3,
  color: theme.color.white,
  textTransform: 'uppercase',
  [theme.mediaQuery.mobileOnly]: {
    display: 'none'
  }
}));

const Footer = styled(Cell)(({ theme }) => ({
  borderTop: `4px solid ${theme.color.lightBackground || theme.color.basis3}`,
  textTransform: 'uppercase'
}));

const ChallengesModal = styled(Modal)(({ theme }) => ({
  backgroundColor: theme.color.white
}));

const MobileLabel = styled('label')(({ theme }) => ({
  fontSize: theme.fontSize.default,
  color: theme.color.basis3,
  textTransform: 'uppercase'
}));

class MyLiveTeamsContent extends React.Component<Props> {
  props: Props;

  static defaultProps = {
    activeSport: undefined,
    lineups: [],
    selectedLineUp: undefined
  };

  componentDidMount() {
    const { pollMyLiveLineUps } = this.props;
    pollMyLiveLineUps();
  }

  componentDidUpdate(prevProps: Props) {
    const { loadLineupChallenges, selectedLineUp, selectedSelectionHash } = this.props;

    if (selectedSelectionHash && prevProps.selectedSelectionHash !== selectedSelectionHash) {
      loadLineupChallenges(selectedLineUp.id, { status: RUNNING });
    }
  }

  componentWillUnmount() {
    const { stopPollingMyLiveLineUps } = this.props;
    stopPollingMyLiveLineUps();
  }

  render() {
    const {
      activeSport,
      handleWidgetClick,
      isLoading,
      onViewContest,
      selectedLineUp,
      setSelectionHash,
      widgetPlayerId,
      lineups
    } = this.props;

    return (
      <div>
        <MyTeamsContent
          activeSport={activeSport}
          isLive
          lineups={lineups}
          status={RUNNING}
          renderEntity={entity => (
            <ClosedEntityRow
              awayTeam={entity.awayAbbr}
              bonus={entity.bonus}
              compressedWidget
              dropped={entity.dropped}
              gameOrder={entity.gameOrder}
              gamePlaced={entity.gamePlaced}
              homeTeam={entity.homeAbbr}
              id={entity.id}
              idStr={entity.idStr}
              image={entity.imageURI || entity.image}
              initials={entity.initials}
              isHome={entity.isHome}
              isTeam={entity.isTeam}
              isRacing={entity.isRacing}
              isScratched={entity.isScratched}
              highlight={entity.highlight}
              key={entity.id}
              labels={entity.labels}
              lineupId={entity.lineupId}
              name={entity.name}
              number={entity.number}
              onWidgetClick={handleWidgetClick}
              popularity={entity.popularity}
              positionId={entity.positionId}
              position={entity.positionAbbr}
              progress={entity.progress}
              reverseOrder={entity.reverseOrder}
              rider={entity.rider}
              riderImage={entity.riderImage}
              score={entity.score}
              showWidget={widgetPlayerId === `${entity.lineupId || ''}${entity.idStr}`}
              statusId={entity.statusId}
              trainer={entity.trainer}
              win={entity.win}
              place={entity.place}
              throttle
            />
          )}
          renderBottomButtons={selectionHash => (
            <Responsive desktop>
              {() => (
                <ContestButtonContainer>
                  <ContestButton
                    onClick={() => {
                      setSelectionHash(selectionHash);
                    }}
                  >
                    <span style={{ paddingRight: '0.1em' }}>{t('challenge')}</span>
                    <Icon type="icon-right-arrow" />
                  </ContestButton>
                </ContestButtonContainer>
              )}
            </Responsive>
          )}
        />
        {selectedLineUp && (
          <ChallengesModal
            bare
            isOpen
            lightCloseButton
            size="medium"
            onCancel={() => {
              setSelectionHash(null);
            }}
          >
            {isLoading ? (
              <Loader pad />
            ) : (
              <React.Fragment>
                <LineUpHeader
                  dark
                  isLive
                  name={selectedLineUp.name}
                  numContests={selectedLineUp.challenges.length}
                  numEntries={selectedLineUp.entryCount}
                  sportIcon={selectedLineUp.sportIcon}
                  startTime={selectedLineUp.startTime}
                  statusId={selectedLineUp.statusId}
                  statusLabel={selectedLineUp.statusLabel}
                />
                <ChallengeTable>
                  <Header align="flex-start" wide>
                    {t('Challenge')}
                  </Header>
                  <Header>Entries</Header>
                  <Header>Winnings</Header>
                  <Header>&nbsp;</Header>
                  {selectedLineUp.challenges.map((ch, i) => (
                    <React.Fragment key={ch.id}>
                      <Cell order={2 + i} align="flex-start" wide>
                        <span>{ch.name}</span>
                      </Cell>
                      <Cell order={2 + i}>
                        <Responsive mobile>
                          <MobileLabel>Entries</MobileLabel>
                        </Responsive>
                        <span>{ch.userEntryCount}</span>
                      </Cell>
                      <Cell order={2 + i}>
                        <Responsive mobile>
                          <MobileLabel>Winnings</MobileLabel>
                        </Responsive>
                        <span>
                          <CurrencyIcon />
                          {numeral(ch.winnings).format('0,0.00')}
                        </span>
                      </Cell>
                      <Cell order={2 + i}>
                        <Button
                          block
                          kind="success"
                          onClick={() => onViewContest(ch.id, get('myEntries[0].id', ch)) || ''}
                        >
                          View
                        </Button>
                      </Cell>
                    </React.Fragment>
                  ))}
                  <Footer order={2 + selectedLineUp.challenges.length} align="flex-end">
                    <span>
                      Total:&nbsp;&nbsp;
                      <CurrencyIcon />
                      {numeral(sumBy('winnings', selectedLineUp.challenges)).format('0,0.00')}
                    </span>
                  </Footer>
                </ChallengeTable>
              </React.Fragment>
            )}
          </ChallengesModal>
        )}
      </div>
    );
  }
}

function mapStateToProps(state, { selectedSelectionHash }) {
  const { running = [] } = myTeamsSelector(state, selectedSelectionHash);

  return {
    isLoading: state.myTeams.isLoading,
    lineups: running,
    selectedLineUp: running.find(lineup => lineup.selectionHash === selectedSelectionHash)
  };
}

const actions = {
  loadLineupChallenges: myTeamsActions.getLineupChallenges,
  pollMyLiveLineUps: myTeamsActions.pollMyLiveLineUps,
  stopPollingMyLiveLineUps: myTeamsActions.stopPollingMyLiveLineUps
};

const connector = connect(mapStateToProps, actions);

export default compose(
  withStateHandlers(
    {
      selectedSelectionHash: null,
      widgetPlayerId: null
    },
    {
      handleWidgetClick: ({ widgetPlayerId }) => id => ({
        widgetPlayerId: id === widgetPlayerId ? null : id
      }),
      setSelectionHash: () => selectedSelectionHash => ({ selectedSelectionHash })
    }
  ),
  connector
)(MyLiveTeamsContent);
